import { useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { Alert, Button, Drawer, Grid, Input, Radio, Skeleton, Typography } from 'antd';
import { standaloneVariant, type EvaluationScenario, type StandaloneVariantResult } from '@/api/scenarioVersions';
import { StatusPill } from '@/components/StatusPill';
import { useStore } from '@/stores';
import { checkName, checkSentence } from '../scenarioWords';
import { mergeScenario, resolveMerge, type MergeResolution } from '../scenarioMerge';
import { cardStyle, surface } from '../review/reviewParts';

const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────────────────
// The advanced editor's two stacked drawers (screen 10, frames D4 and D5).
// ScenarioMergeDrawer: Original / Yours / Current for every field both of you changed; nothing is used until every
// conflict is chosen, and the merged result goes back to the editor as an unsaved edit on top of Current, which the
// server saves only if it compiles. StandaloneVariantDrawer: the server's preview of the variant, then its creation;
// the original stays as it is.
// ─────────────────────────────────────────────────────────────────────────────

export const ScenarioMergeDrawer = observer(function ScenarioMergeDrawer({
  open, onClose, original, yours, current, savedBy, onUse,
}: {
  open: boolean;
  onClose: () => void;
  original: EvaluationScenario;
  yours: EvaluationScenario;
  current: EvaluationScenario;
  savedBy: string;
  onUse: (merged: EvaluationScenario) => void;
}) {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  const narrow = !Grid.useBreakpoint().md;
  const merge = useMemo(() => mergeScenario(original, yours, current), [original, yours, current]);
  const [choices, setChoices] = useState<Record<string, MergeResolution | undefined>>({});
  useEffect(() => setChoices({}), [original, yours, current]);
  const merged = resolveMerge(original, yours, current, choices);
  const decided = merge.conflicts.filter((c) => choices[c.key] && (choices[c.key]!.choice !== 'manual' || choices[c.key]!.text?.trim())).length;
  const s = surface(isDark);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      size="min(960px, 100vw)"
      title={<div><div>Merge your changes</div><Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>{savedBy} saved a newer version while you edited</Text></div>}
      styles={{ body: { background: s.page, display: 'flex', flexDirection: 'column', gap: 12 } }}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <Text type="secondary" style={{ fontSize: 12 }}>{decided} of {merge.conflicts.length} chosen. The merged result must compile before it can be saved.</Text>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button onClick={onClose}>Cancel</Button>
            <Button type="primary" disabled={!merged} onClick={() => merged && onUse(merged)}>Use the merged result</Button>
          </div>
        </div>
      }
    >
      <Alert type="info" showIcon title={`${merge.clean} of your changed parts merge cleanly; ${merge.conflicts.length} ${merge.conflicts.length === 1 ? 'was' : 'were'} changed by both of you`}
        description="Parts only you changed keep your change; parts only they changed keep theirs. Choose for each part below. Saving over their version without merging is not offered." />
      {merge.conflicts.map((c) => {
        const r = choices[c.key];
        return (
          <section key={c.key} style={cardStyle(isDark)} aria-label={c.label}>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>{c.label}</Text>
            <div style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : 'repeat(3, minmax(0, 1fr))', gap: 8 }}>
              {([['Original', c.original], ['Yours', c.yours], ['Current', c.current]] as const).map(([name, text]) => (
                <div key={name} style={{ border: `1px solid ${s.line}`, borderRadius: 8, padding: 8 }}>
                  <Text type="secondary" style={{ fontSize: 11, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase' }}>{name}</Text>
                  <div style={{ fontSize: 13, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{text}</div>
                </div>
              ))}
            </div>
            <Radio.Group
              style={{ marginTop: 8 }}
              value={r?.choice}
              onChange={(e) => setChoices((prev) => ({ ...prev, [c.key]: { choice: e.target.value, text: e.target.value === 'manual' ? prev[c.key]?.text ?? c.yours : undefined } }))}
              options={[
                { value: 'yours', label: 'Keep yours' },
                { value: 'current', label: 'Keep theirs' },
                ...(c.manual ? [{ value: 'manual', label: 'Merge manually' }] : []),
              ]}
            />
            {r?.choice === 'manual' && (
              <Input.TextArea style={{ marginTop: 8 }} autoSize={{ minRows: 2, maxRows: 6 }} value={r.text}
                onChange={(e) => setChoices((prev) => ({ ...prev, [c.key]: { choice: 'manual', text: e.target.value } }))} aria-label={`Merged text for ${c.label}`} />
            )}
          </section>
        );
      })}
    </Drawer>
  );
});

export const StandaloneVariantDrawer = observer(function StandaloneVariantDrawer({
  open, onClose, draft, baseHash, originalProtocolId, originalNumber, onCreated,
}: {
  open: boolean;
  onClose: () => void;
  draft: EvaluationScenario;
  baseHash: string;
  originalProtocolId: number;
  originalNumber?: number;
  onCreated: (newScenarioId: string) => void;
}) {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  const [preview, setPreview] = useState<StandaloneVariantResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const request = useRef(0);

  useEffect(() => {
    if (!open) return;
    const mine = ++request.current;
    setPreview(null);
    setError(null);
    standaloneVariant(draft, baseHash, true)
      .then((p) => mine === request.current && setPreview(p))
      .catch((e: Error) => mine === request.current && setError(e.message));
  }, [open, draft, baseHash]);

  const create = async () => {
    setCreating(true);
    setError(null);
    try {
      const made = await standaloneVariant(draft, baseHash, false);
      if (made.created && made.newScenarioId) onCreated(made.newScenarioId);
      else setPreview(made);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setCreating(false);
    }
  };

  const dropped = draft.checks.filter((c) => preview?.droppedCheckIds.includes(c.id));
  const card = cardStyle(isDark);
  return (
    <Drawer
      open={open}
      onClose={onClose}
      size="min(640px, 100vw)"
      title={<div><div>Create standalone variant</div><Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>A new scenario without a protocol; the original stays as it is</Text></div>}
      styles={{ body: { background: surface(isDark).page, display: 'flex', flexDirection: 'column', gap: 12 } }}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" loading={creating} disabled={!preview || preview.problems.length > 0} onClick={() => void create()}>Create the variant</Button>
        </div>
      }
    >
      {error && <Alert type="error" showIcon title="Could not make the variant" description={error} />}
      {!preview && !error && <Skeleton active paragraph={{ rows: 5 }} />}
      {preview && preview.problems.length > 0 && (
        <Alert type="error" showIcon title="The variant cannot be made yet"
          description={<ul style={{ margin: 0, paddingLeft: 18 }}>{preview.problems.map((p) => <li key={p}>{p}</li>)}</ul>} />
      )}
      {preview && preview.problems.length === 0 && (
        <>
          <section style={card} aria-label="Variant">
            <Text type="secondary" style={{ fontSize: 12 }}>Standalone variant · preview</Text>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13, fontWeight: 600, wordBreak: 'break-all' }}>{preview.newScenarioId}</div>
          </section>
          <section style={card} aria-label="Original">
            <Text strong>Original</Text>
            <div>Kept as it is: version {originalNumber ?? '?'}, with its protocol #{originalProtocolId}.</div>
          </section>
          <section style={card} aria-label="Affected">
            <Text strong>Affected</Text>
            {dropped.length === 0 ? (
              <div>No check needs protocol facts; every check carries over.</div>
            ) : (
              dropped.map((c) => <div key={c.id} style={{ fontSize: 13 }}>{checkName(draft, c)}: {checkSentence(c)} <StatusPill tone="red">left out</StatusPill></div>)
            )}
            <div style={{ marginTop: 4, fontSize: 12.5 }}>{preview.keptCheckIds.length} check(s) carry over.</div>
          </section>
          <section style={card} aria-label="Coverage lost">
            <Text strong>Coverage lost</Text>
            <div>{dropped.length === 0 ? 'None.' : 'What the left-out checks tested is no longer covered by the variant. The original keeps covering it.'}</div>
          </section>
          <section style={card} aria-label="Review">
            <Text strong>Review</Text>
            <div>The variant starts as a new, unapproved version 1. It needs review before its results count.</div>
            <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 4 }}>
              A result where every check is Not applicable never reads as a success: the server refuses such a version.
            </Text>
          </section>
        </>
      )}
    </Drawer>
  );
});
